import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../types';
import { startGeneration, getMessageStatus, fetchChatMessages, createChat, regenerateMessage, editUserMessage, fetchUser } from '../api';
import styles from './ChatApp.module.css';

interface ChatAreaProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  activeChatUuid?: string;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  currentModel: string;
  setIsScrollButtonVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onBalanceUpdate: (balance: number) => void; // Новый пропс для обновления баланса
}

const ThinkComponent = ({ content }: { content: string }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className={styles.thinkWrapper}>
      <div className={styles.thinkHeader}>
        <button className={styles.collapseBtn} onClick={() => setIsCollapsed(!isCollapsed)}>
          Мысли модели
          {isCollapsed ? <i className="fas fa-chevron-up"></i> : <i className="fas fa-chevron-down"></i>}
        </button>
      </div>
      <div className={`${styles.thinkContent} ${isCollapsed ? styles.collapsed : ''}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </div>
  );
};

const ChatArea = ({
  messages,
  setMessages,
  activeChatUuid,
  isGenerating,
  setIsGenerating,
  currentModel,
  setIsScrollButtonVisible,
  onBalanceUpdate, // Добавляем пропс в компонент
}: ChatAreaProps) => {
  const [input, setInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isScrollButtonVisible, setLocalIsScrollButtonVisible] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const MAX_FILES = 5;

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 288;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    if (newHeight === maxHeight) textarea.scrollTop = textarea.scrollHeight;
  };

  const handleInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendNewMessage();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const cursorPos = textarea.selectionStart;
      const newInput = input.substring(0, cursorPos) + '\n' + input.substring(cursorPos);
      setInput(newInput);
      setTimeout(() => adjustTextareaHeight(textarea), 0);
    } else {
      setInput(textarea.value);
      adjustTextareaHeight(textarea);
    }
  };

  useEffect(() => {
    adjustTextareaHeight(textareaRef.current);
  }, [input]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setAttachedFiles((prevFiles) => {
        const existingNames = new Set(prevFiles.map((file) => file.name));
        const uniqueNewFiles = newFiles.filter((file) => !existingNames.has(file.name));
        const totalFiles = prevFiles.length + uniqueNewFiles.length;
        if (totalFiles > MAX_FILES) {
          alert(`Максимальное количество файлов: ${MAX_FILES}. Выбрано ${totalFiles}, будут добавлены только первые ${MAX_FILES}.`);
          return [...prevFiles, ...uniqueNewFiles].slice(0, MAX_FILES);
        }
        return [...prevFiles, ...uniqueNewFiles];
      });
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev: File[]) => prev.filter((_, i) => i !== index));
  };

  const truncateFileName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name;
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    return `${nameWithoutExt.substring(0, maxLength - 3 - (extension?.length || 0))}...${extension ? `.${extension}` : ''}`;
  };

  const copyMessage = (content: string, button: HTMLButtonElement) => {
    navigator.clipboard.writeText(content).then(() => {
      button.classList.add(styles.actionBtnCopied);
      button.innerHTML = '<i class="fas fa-check"></i>';
      setTimeout(() => {
        button.classList.remove(styles.actionBtnCopied);
        button.innerHTML = '<i class="fas fa-copy"></i>';
      }, 1500);
    }).catch((err) => {
      console.error('Ошибка копирования:', err);
    });
  };

  const copyCode = (e: React.MouseEvent<HTMLElement>) => {
    const codeElement = e.currentTarget;
    const text = codeElement.textContent || '';
    navigator.clipboard.writeText(text).then(() => {
      codeElement.classList.add(styles.codeCopied);
      setTimeout(() => {
        codeElement.classList.remove(styles.codeCopied);
      }, 1500);
    }).catch((err) => {
      console.error('Ошибка копирования кода:', err);
    });
  };

  const copyCodeBlock = (content: string, button: HTMLDivElement) => {
    navigator.clipboard.writeText(content).then(() => {
      button.textContent = 'Скопировано!';
      button.classList.add(styles.codeBlockCopied);
      setTimeout(() => {
        button.textContent = 'Копировать';
        button.classList.remove(styles.codeBlockCopied);
      }, 1500);
    }).catch((err) => {
      console.error('Ошибка копирования блока кода:', err);
    });
  };

  const detectLanguage = (content: string): string => {
    if (content.includes('function') || content.includes('const') || content.includes('let')) return 'javascript';
    if (content.includes('def ') || content.includes('print(')) return 'python';
    if (content.includes('import ') && content.includes('from ')) return 'typescript';
    return 'text';
  };

  const sendNewMessage = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || isGenerating) return;
    setInput('');
    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    setIsGenerating(true);
    setTimeout(() => adjustTextareaHeight(textareaRef.current), 0);

    try {
      let chatUuid: string; // Явно задаем тип как string
      if (activeChatUuid) {
        chatUuid = activeChatUuid;
      } else {
        const newChat = await createChat();
        chatUuid = newChat.uuid; // Используем uuid из ответа createChat
        navigate(`/chat/${chatUuid}`);
      }

      const formData = new FormData();
      formData.append('chat_uuid', chatUuid);
      formData.append('message', input);
      filesToSend.forEach((file) => formData.append('files', file));

      const { user_message_id, message_id, model } = await startGeneration(chatUuid, formData);
      console.log(`Started generation: user_message_id=${user_message_id}, message_id=${message_id}, model=${model}`);

      const attachedFilesData = filesToSend.map(file => ({
        name: file.name,
        data: '',
        size: file.size,
        type: file.type,
      }));

      const userMessage: Message = {
        id: user_message_id,
        content: input || 'Прикрепленные файлы',
        is_user: true,
        is_complete: true,
        created_at: new Date().toISOString(),
        attachedFiles: attachedFilesData,
        children: [{
          id: message_id,
          content: 'Генерация в процессе, подождите пару секунд...',
          is_user: false,
          is_complete: false,
          created_at: new Date().toISOString(),
          children: [],
          model: model || currentModel,
        }],
      };
      setMessages((prev) => [...prev, userMessage]);

      intervalRef.current = setInterval(async () => {
        try {
          const status = await getMessageStatus(message_id);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.children.some((child) => child.id === message_id)
                ? {
                  ...msg,
                  children: msg.children.map((child) =>
                    child.id === message_id
                      ? {
                        ...child,
                        content: status.content ? status.content : 'Генерация в процессе, подождите пару секунд...',
                        tokens: status.is_complete ? status.tokens : undefined,
                        cost: status.is_complete ? status.cost : undefined,
                        is_complete: status.is_complete,
                        model: status.model || model || currentModel,
                      }
                      : child
                  ),
                }
                : msg
            )
          );

          if (status.is_complete) {
            clearInterval(intervalRef.current!);
            setIsGenerating(false);
            const finalMessages = await fetchChatMessages(chatUuid); // Теперь TypeScript знает, что chatUuid — string
            setMessages(finalMessages);
            const userData = await fetchUser();
            onBalanceUpdate(userData.balance);
          }
        } catch (error) {
          console.error(`Ошибка в getMessageStatus для message_id=${message_id}:`, error);
          clearInterval(intervalRef.current!);
          setIsGenerating(false);
          const syncedMessages = await fetchChatMessages(chatUuid); // Теперь TypeScript знает, что chatUuid — string
          setMessages(syncedMessages);
          const userData = await fetchUser();
          onBalanceUpdate(userData.balance);
        }
      }, 500);
    } catch (error: any) {
      console.error('Ошибка при отправке сообщения:', error);
      setIsGenerating(false);
      if (error.response?.status === 429) {
        navigate('/login', { state: { from: window.location.pathname } });
      } else if (error.response?.status === 402) {
        alert('Недостаточно средств на балансе для отправки сообщения.');
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      } else if (activeChatUuid) {
        const syncedMessages = await fetchChatMessages(activeChatUuid);
        setMessages(syncedMessages);
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      }
    }
  };

  const regenerateLastBotMessage = async (messageId: string) => {
    if (isGenerating || !activeChatUuid) return;
    setIsGenerating(true);

    try {
      const { message_id, model } = await regenerateMessage(messageId);
      console.log(`Regenerated message_id=${message_id}, model=${model}`);

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          children: msg.children.map((child) =>
            child.id === messageId
              ? { ...child, id: message_id, content: 'Генерация в процессе, подождите пару секунд...', is_complete: false, model: model || currentModel }
              : child
          ),
        }))
      );

      intervalRef.current = setInterval(async () => {
        try {
          const status = await getMessageStatus(message_id);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.children.some((child) => child.id === message_id)
                ? {
                  ...msg,
                  children: msg.children.map((child) =>
                    child.id === message_id
                      ? {
                        ...child,
                        content: status.content ? status.content : 'Генерация в процессе, подождите пару секунд...',
                        tokens: status.is_complete ? status.tokens : undefined,
                        cost: status.is_complete ? status.cost : undefined,
                        is_complete: status.is_complete,
                        model: status.model || model || currentModel,
                      }
                      : child
                  ),
                }
                : msg
            )
          );

          if (status.is_complete) {
            clearInterval(intervalRef.current!);
            setIsGenerating(false);
            const finalMessages = await fetchChatMessages(activeChatUuid);
            setMessages(finalMessages);
            // Обновляем баланс после регенерации
            const userData = await fetchUser();
            onBalanceUpdate(userData.balance);
          }
        } catch (error) {
          console.error(`Ошибка в getMessageStatus для message_id=${message_id}:`, error);
          clearInterval(intervalRef.current!);
          setIsGenerating(false);
          const syncedMessages = await fetchChatMessages(activeChatUuid);
          setMessages(syncedMessages);
          const userData = await fetchUser();
          onBalanceUpdate(userData.balance);
        }
      }, 500);
    } catch (error: any) {
      console.error('Ошибка при перегенерации:', error);
      setIsGenerating(false);
      if (error.response?.status === 429) {
        navigate('/login', { state: { from: window.location.pathname } });
      } else if (error.response?.status === 402) {
        alert('Недостаточно средств на балансе для перегенерации.');
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      } else if (activeChatUuid) {
        const syncedMessages = await fetchChatMessages(activeChatUuid);
        setMessages(syncedMessages);
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      }
    }
  };

  const stopGeneration = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsGenerating(false);
    if (activeChatUuid) {
      fetchChatMessages(activeChatUuid).then(setMessages).catch((err) => console.error('Ошибка синхронизации:', err));
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (editingMessageId) {
      const textarea = document.querySelector(`.${styles.messageInput}`) as HTMLTextAreaElement;
      adjustTextareaHeight(textarea);
    }
  }, [editingMessageId]);

  const editMessage = (id: string, content: string) => {
    if (isGenerating) return;
    setEditingMessageId(id);
    setEditContent(content);
    adjustTextareaHeight(textareaRef.current);
  };

  const saveEdit = async (id: string) => {
    if (!editContent.trim() || !activeChatUuid) return;
    setIsGenerating(true);

    try {
      const { user_message_id, message_id, model } = await editUserMessage(id, editContent);
      console.log(`Edited user_message_id=${user_message_id}, new message_id=${message_id}, model=${model}`);

      setEditingMessageId(null);
      setEditContent('');

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === user_message_id
            ? {
              ...msg,
              content: editContent,
              children: [{
                id: message_id,
                content: 'Генерация в процессе, подождите пару секунд...',
                is_user: false,
                is_complete: false,
                created_at: new Date().toISOString(),
                children: [],
                model: model || currentModel,
              }],
            }
            : msg
        )
      );

      intervalRef.current = setInterval(async () => {
        try {
          const status = await getMessageStatus(message_id);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.children.some((child) => child.id === message_id)
                ? {
                  ...msg,
                  children: msg.children.map((child) =>
                    child.id === message_id
                      ? {
                        ...child,
                        content: status.content ? status.content : 'Генерация в процессе, подождите пару секунд...',
                        tokens: status.is_complete ? status.tokens : undefined,
                        cost: status.is_complete ? status.cost : undefined,
                        is_complete: status.is_complete,
                        model: status.model || model || currentModel,
                      }
                      : child
                  ),
                }
                : msg
            )
          );

          if (status.is_complete) {
            clearInterval(intervalRef.current!);
            setIsGenerating(false);
            const finalMessages = await fetchChatMessages(activeChatUuid);
            setMessages(finalMessages);
            // Обновляем баланс после редактирования
            const userData = await fetchUser();
            onBalanceUpdate(userData.balance);
          }
        } catch (error) {
          console.error(`Ошибка в getMessageStatus для message_id=${message_id}:`, error);
          clearInterval(intervalRef.current!);
          setIsGenerating(false);
          const syncedMessages = await fetchChatMessages(activeChatUuid);
          setMessages(syncedMessages);
          const userData = await fetchUser();
          onBalanceUpdate(userData.balance);
        }
      }, 500);
    } catch (error: any) {
      console.error('Ошибка при редактировании:', error);
      setIsGenerating(false);
      if (error.response?.status === 429) {
        navigate('/login', { state: { from: window.location.pathname } });
      } else if (error.response?.status === 402) {
        alert('Недостаточно средств на балансе для редактирования.');
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      } else if (activeChatUuid) {
        const syncedMessages = await fetchChatMessages(activeChatUuid);
        setMessages(syncedMessages);
        const userData = await fetchUser();
        onBalanceUpdate(userData.balance);
      }
    }
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const updateScrollDownButton = () => {
    const wrapper = contentWrapperRef.current;
    if (wrapper) {
      const { scrollTop, scrollHeight, clientHeight } = wrapper;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setLocalIsScrollButtonVisible(!isAtBottom);
      setIsScrollButtonVisible(!isAtBottom);
    }
  };

  const scrollToBottom = () => {
    const wrapper = contentWrapperRef.current;
    if (wrapper) wrapper.scrollTo({ top: wrapper.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    const contentWrapper = contentWrapperRef.current;
    if (contentWrapper) {
      contentWrapper.scrollTop = contentWrapper.scrollHeight;
      contentWrapper.addEventListener('scroll', updateScrollDownButton);
      updateScrollDownButton();
    }
    return () => {
      if (contentWrapper) contentWrapper.removeEventListener('scroll', updateScrollDownButton);
    };
  }, [messages]);

  const isMessageComplete = (message: Message) => {
    return !message.is_user && message.is_complete;
  };

  const lastUserMessage = messages.filter((msg) => msg.is_user).slice(-1)[0];
  const lastBotMessage = messages.flatMap((msg) => [msg, ...msg.children]).filter((msg) => !msg.is_user && msg.is_complete).slice(-1)[0];

  const parseMessageContent = (content: string, message: Message) => {
    const parts: React.JSX.Element[] = [];
    const regex = /<think>(.*?)<\/think>/gs;
    let lastIndex = 0;

    content.replace(regex, (match, thinkContent, index) => {
      if (index > lastIndex) {
        const beforeText = content.slice(lastIndex, index);
        if (message.is_user) {
          parts.push(<span key={`text-${lastIndex}`}>{beforeText}</span>);
        } else {
          parts.push(
            <ReactMarkdown
              key={`text-${lastIndex}`}
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, className, children, ...props }) {
                  const content = children?.toString() || '';
                  if (!node?.position) {
                    return <code className={className} {...props}>{content}</code>;
                  }
                  const isInline = node.position.start.line === node.position.end.line;
                  if (isInline) {
                    return (
                      <code
                        className={`${className || ''} ${styles.inlineCode}`}
                        onClick={copyCode}
                        {...props}
                      >
                        {content}
                      </code>
                    );
                  }
                  return (
                    <div className={styles.codeBlockWrapper}>
                      <div className={styles.codeBlockBanner}>
                        <div className={styles.codeBlockInfo}>{detectLanguage(content)}</div>
                        <div className={styles.codeBlockAction}>
                          <div
                            className={styles.codeBlockCopyButton}
                            onClick={(e) => copyCodeBlock(content, e.currentTarget)}
                          >
                            Копировать
                          </div>
                        </div>
                      </div>
                      <pre className={styles.codeBlock}>
                        <code className={className} {...props}>{content}</code>
                      </pre>
                    </div>
                  );
                },
              }}
            >
              {beforeText}
            </ReactMarkdown>
          );
        }
      }

      parts.push(<ThinkComponent key={`think-${index}`} content={thinkContent} />);

      lastIndex = index + match.length;
      return match;
    });

    if (lastIndex < content.length) {
      const remainingText = content.slice(lastIndex);
      if (message.is_user) {
        parts.push(<span key={`text-${lastIndex}`}>{remainingText}</span>);
      } else {
        parts.push(
          <ReactMarkdown
            key={`text-${lastIndex}`}
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, className, children, ...props }) {
                const content = children?.toString() || '';
                if (!node?.position) {
                  return <code className={className} {...props}>{content}</code>;
                }
                const isInline = node.position.start.line === node.position.end.line;
                if (isInline) {
                  return (
                    <code
                      className={`${className || ''} ${styles.inlineCode}`}
                      onClick={copyCode}
                      {...props}
                    >
                      {content}
                    </code>
                  );
                }
                return (
                  <div className={styles.codeBlockWrapper}>
                    <div className={styles.codeBlockBanner}>
                      <div className={styles.codeBlockInfo}>{detectLanguage(content)}</div>
                      <div className={styles.codeBlockAction}>
                        <div
                          className={styles.codeBlockCopyButton}
                          onClick={(e) => copyCodeBlock(content, e.currentTarget)}
                        >
                          Копировать
                        </div>
                      </div>
                    </div>
                    <pre className={styles.codeBlock}>
                      <code className={className} {...props}>{content}</code>
                    </pre>
                  </div>
                );
              },
            }}
          >
            {remainingText}
          </ReactMarkdown>
        );
      }
    }

    if (parts.length === 0) {
      if (message.is_user) {
        parts.push(<span key="text-full">{content}</span>);
      } else {
        parts.push(
          <ReactMarkdown
            key="text-full"
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, className, children, ...props }) {
                const content = children?.toString() || '';
                if (!node?.position) {
                  return <code className={className} {...props}>{content}</code>;
                }
                const isInline = node.position.start.line === node.position.end.line;
                if (isInline) {
                  return (
                    <code
                      className={`${className || ''} ${styles.inlineCode}`}
                      onClick={copyCode}
                      {...props}
                    >
                      {content}
                    </code>
                  );
                }
                return (
                  <div className={styles.codeBlockWrapper}>
                    <div className={styles.codeBlockBanner}>
                      <div className={styles.codeBlockInfo}>{detectLanguage(content)}</div>
                      <div className={styles.codeBlockAction}>
                        <div
                          className={styles.codeBlockCopyButton}
                          onClick={(e) => copyCodeBlock(content, e.currentTarget)}
                        >
                          Копировать
                        </div>
                      </div>
                    </div>
                    <pre className={styles.codeBlock}>
                      <code className={className} {...props}>{content}</code>
                    </pre>
                  </div>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
        );
      }
    }

    return parts;
  };

  const renderMessage = (message: Message, level: number = 0) => {
    return (
      <React.Fragment key={message.id}>
        <div
          className={`${styles.messageRow} ${message.is_user ? styles.messageRowUser : styles.messageRowAssistant}`}
        >
          {message.is_user && (
            <>
              <button
                className={styles.actionBtn}
                onClick={(e) => copyMessage(message.content, e.currentTarget)}
                disabled={isGenerating}
              >
                <i className="fas fa-copy"></i>
              </button>
              {message.id === lastUserMessage?.id && (
                <button
                  className={styles.actionBtn}
                  onClick={() => editMessage(message.id, message.content)}
                  disabled={isGenerating}
                >
                  <i className="fas fa-edit"></i>
                </button>
              )}
            </>
          )}
          <div className={styles.messageBubble} data-message-id={message.id}>
            {editingMessageId === message.id ? (
              <>
                <textarea
                  className={styles.messageInput}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      saveEdit(message.id);
                    }
                  }}
                  onInput={(e) => adjustTextareaHeight(e.currentTarget)}
                  autoFocus
                />
                <div className={styles.messageActions}>
                  <button className={`${styles.modalBtn} ${styles.cancelBtn}`} onClick={cancelEdit}>
                    Отмена
                  </button>
                  <button className={`${styles.modalBtn} ${styles.saveBtn}`} onClick={() => saveEdit(message.id)}>
                    Сохранить
                  </button>
                </div>
              </>
            ) : (
              <>
                {parseMessageContent(message.content, message)}
                {message.attachedFiles && message.attachedFiles.length > 0 && (
                  <div className={styles.attachedFiles}>
                    {message.attachedFiles.map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                        <i className="fas fa-file"></i>
                        <span className={styles.fileName}>{truncateFileName(file.name)}</span>
                        <span className={styles.fileSize}>{(file.size / 1024).toFixed(2)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
                {!message.is_user && isMessageComplete(message) && (
                  <div className={styles.messageInfo}>
                    <div className={styles.messageStats}>
                      <span>{message.model || currentModel}</span>
                      <span className={styles.separator}>•</span>
                      <span>Токены: {message.tokens || 0}</span>
                      <span className={styles.separator}>•</span>
                      <span>Стоимость: ₽{Number(message.cost || 0).toFixed(2)}</span>
                    </div>
                    <div className={styles.messageActions}>
                      <button
                        className={styles.actionBtn}
                        onClick={(e) => copyMessage(message.content, e.currentTarget)}
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                      {message.id === lastBotMessage?.id && (
                        <button
                          className={styles.actionBtn}
                          onClick={() => regenerateLastBotMessage(message.id)}
                          disabled={isGenerating}
                        >
                          <i className="fas fa-redo"></i>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {message.children.map((child) => renderMessage(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <section className={styles.contentWrapper} ref={contentWrapperRef}>
        <div className={styles.chatMessages}>
          {messages.length === 0 && !activeChatUuid ? (
            <div className={styles.promptSuggestions}>
              <h3>Добро пожаловать!</h3>
              <div className={styles.infoColumns}>
                <div className={styles.infoColumn}>
                  <h4><i className="fas fa-robot"></i> Что умеет бот?</h4>
                  <p>Этот бот — ваш универсальный помощник! Он может:</p>
                  <ul>
                    <li><i className="fas fa-comment"></i> Отвечать на вопросы и вести беседу</li>
                    <li><i className="fas fa-pen"></i> Писать тексты: письма, истории, заметки</li>
                    <li><i className="fas fa-utensils"></i> Делиться рецептами и советами</li>
                    <li><i className="fas fa-lightbulb"></i> Предлагать идеи и интересные факты</li>
                    <li><i className="fas fa-film"></i> Рекомендовать фильмы, книги и развлечения</li>
                  </ul>
                </div>
                <div className={styles.infoColumn}>
                  <h4><i className="fas fa-tools"></i> Как пользоваться?</h4>
                  <p>Всё просто:</p>
                  <ul>
                    <li><i className="fas fa-keyboard"></i> Введите ваш вопрос или задание в поле ниже</li>
                    <li><i className="fas fa-paper-plane"></i> Нажмите кнопку отправки или Enter</li>
                    <li><i className="fas fa-edit"></i> Редактируйте свои сообщения, если нужно</li>
                    <li><i className="fas fa-redo"></i> Перегенерируйте ответы для новых вариантов</li>
                    <li><i className="fas fa-question-circle"></i> Задавайте любые вопросы!</li>
                  </ul>
                </div>
              </div>
              <div className={styles.promptSection}>
                <h4><i className="fas fa-rocket"></i> Попробуйте эти запросы:</h4>
                <div className={styles.promptGrid}>
                  <button className={styles.promptCard} onClick={() => setInput('Объясни простыми словами, что такое искусственный интеллект')}>
                    Что такое ИИ?
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Расскажи, как пользоваться чатом с ИИ')}>
                    Как пользоваться чатом?
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Напиши шаблон письма для поздравления с днем рождения')}>
                    Письмо-поздравление
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Придумай сказку про робота и дракона')}>
                    Сказка о роботе
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Объясни, как работает калькулятор без математики')}>
                    Как работает калькулятор?
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Расскажи, что такое гравитация простыми словами')}>
                    Что такое гравитация?
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Придумай три идеи для настольной игры')}>
                    Идеи для игры
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Дай совет, как расслабиться после долгого дня')}>
                    Как расслабиться?
                  </button>
                  <button className={styles.promptCard} onClick={() => setInput('Составь список дел на утро для хорошего настроения')}>
                    Утренний список дел
                  </button>
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => renderMessage(message))
          )}
        </div>
      </section>
      <button
        className={styles.scrollDownBtn}
        style={{ display: isScrollButtonVisible ? 'flex' : 'none' }}
        onClick={scrollToBottom}
      >
        <i className="fas fa-chevron-down"></i>
      </button>
      <div className={styles.inputArea}>
        <div className={styles.inputContainer}>
          <textarea
            className={styles.messageInput}
            placeholder="Введите сообщение..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInput}
            ref={textareaRef}
            rows={1}
          />
          {attachedFiles.length > 0 && (
            <div className={styles.attachedFilesPreview}>
              {attachedFiles.map((file, index) => (
                <div key={index} className={styles.filePreview}>
                  <i className="fas fa-file"></i>
                  <span className={styles.fileName}>{truncateFileName(file.name)}</span>
                  <button
                    className={styles.removeFileBtn}
                    onClick={() => removeFile(index)}
                    disabled={isGenerating}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className={styles.inputButtons}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
            />
            <button
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={isGenerating}
            >
              <i className="fas fa-paperclip"></i>
            </button>
            <button
              className={styles.sendBtn}
              onClick={isGenerating ? stopGeneration : sendNewMessage}
              disabled={isGenerating && !intervalRef.current}
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
        <div className={styles.inputFooter}>
          <a href="/privacy-policy" className={styles.footerLink} target="_blank" rel="noopener noreferrer">
            Политика конфиденциальности
          </a>
          <span className={styles.footerSep}>•</span>
          <a href="/terms-of-use" className={styles.footerLink} target="_blank" rel="noopener noreferrer">
            Условия использования
          </a>
        </div>
      </div>
    </>
  );
};

export default ChatArea;